import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { message } from 'antd'

import TodoHeader from "../components/TodoHeader"
import TodoTable from "../components/TodoTable"
import TodoTool from "../components/TodoTool"
import TimeUtils from "../utils"
import deviceApi from "../services/api"
import { setTheme as setThemeUtil, getCurrentTheme } from "../utils/theme"
import { elementToText } from "../utils/richText"
import './edit.css'

function Edit() {
    const params = useParams()
    const navigate = useNavigate()
    const date = params.date || TimeUtils.today()

    const ref = React.useRef(null)
    const [loading, setLoading] = React.useState(true)
    const [saving, setSaving] = React.useState(false) // eslint-disable-line no-unused-vars
    const [data, setData] = React.useState({
        time: date,
        blur: false,
        extra2Text: "",
        extra3Links: "",
        note: "",
        items: [],
    })
    // 单独放一个 state：切开关时不动 data，避免 React 重渲染影响 contentEditable 里已输入的内容
    const [noteIsPublic, setNoteIsPublic] = React.useState(true)

    // 加载数据
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        loadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date])

    const loadData = async () => {
        setLoading(true)
        try {
            const currentDate = params.date || TimeUtils.today()
            const resp = await deviceApi.getEdit(currentDate)
            const items = resp.items && resp.items.length > 0
                ? resp.items
                : [{ id: 1, project: '', time: '', score: 1, is_public: true, extra: { plan: '', done: '', summary: '' } }]
            const blurState = resp.blur || false
            setData({
                time: resp.time || currentDate,
                blur: blurState,
                extra2Text: resp.extra2Text || "",
                extra3Links: resp.extra3Links || "",
                note: resp.note || "",
                items: items,
            })
            setNoteIsPublic(resp.note_is_public !== false)
            if (blurState) {
                setTimeout(() => {
                    const blurElements = ref.current?.querySelectorAll('.ne-table-inner-wrap, .container-todo, .container-limit, tbody.ne-table-inner')
                    blurElements?.forEach(el => {
                        el.classList.add('blur')
                    })
                }, 0)
            }
        } catch (error) {
            message.error(`加载数据失败: ${error.message}`)
            setData({
                time: date,
                blur: false,
                extra2Text: "",
                extra3Links: "",
                note: "",
                items: [{ id: 1, project: '', time: '', score: 0, content: '' }],
            })
            setNoteIsPublic(true)
        } finally {
            setLoading(false)
        }
    }

    // 处理输入变化
    const inputMethod = () => {
        const container = ref.current
        if (!container) return

        const todoElement = container.querySelector('.container-todo')
        let noteContent = ''
        if (todoElement) {
            noteContent = todoElement.innerText || todoElement.textContent || ''
        }

        const tbodyList = container.querySelectorAll("tbody.ne-table-inner")
        const total = []

        tbodyList.forEach(tbody => {
            const firstEditCell = tbody.querySelector('.ne-edit')
            if (!firstEditCell) return

            const rows = tbody.querySelectorAll('.ne-tr')
            if (!rows.length) return

            const firstRow = rows[0]
            const projectCell = firstRow.querySelector('[data-col="0"]')
            const visibilitySelect = firstRow.querySelector('.ne-visibility')
            const timeCell = firstRow.querySelector('[data-col="2"]')
            const scoreSelect = firstRow.querySelector('.score.score')

            const extraCells = []
            rows.forEach(row => {
                const cell = row.querySelector('[data-col="4"]')
                if (cell) extraCells.push(cell)
            })

            const project = elementToText(projectCell)
            const time = elementToText(timeCell)
            const score = parseInt(scoreSelect?.value) || 0
            const isPublic = visibilitySelect ? visibilitySelect.value !== '0' : true
            const extraPlan = elementToText(extraCells[0])
            const extraDone = elementToText(extraCells[1])
            const extraSummary = elementToText(extraCells[2])

            if (score !== 0) {
                total.push({
                    project,
                    time,
                    score,
                    is_public: isPublic,
                    extra: {
                        plan: extraPlan,
                        done: extraDone,
                        summary: extraSummary,
                    },
                })
            }
        })

        return {
            date: data.time,
            note: noteContent,
            dataList: total,
        }
    }

    const handleNoteVisibilityChange = (checked) => {
        setNoteIsPublic(checked)
    }

    // 保存数据
    const handleSave = async () => {
        const saveData = inputMethod()
        setSaving(true)
        try {
            await deviceApi.save(data.time, {
                date: saveData.date,
                note: saveData.note,
                note_is_public: noteIsPublic,
                dataList: saveData.dataList
            })
            message.success('保存成功')
        } catch (error) {
            message.error(`保存失败: ${error.message}`)
        } finally {
            setSaving(false)
        }
    }

    // 按钮事件处理
    const buttonAction = (eventName) => {
        const event = {
            "tool-add": () => {
                const maxId = data.items.length > 0
                    ? Math.max(...data.items.map(item => item.id || 0))
                    : 0
                const newItem = {
                    id: maxId + 1,
                    project: '',
                    time: '',
                    score: 1,
                    is_public: true,
                    extra: { plan: '', done: '', summary: '' }
                }
                setData(prev => ({
                    ...prev,
                    items: [...prev.items, newItem]
                }))
            },

            "tool-save": () => {
                handleSave()
            },

            "tool-yesterday": () => {
                const yesterday = TimeUtils.getPreviousDay(data.time)
                navigate(`/detail/${yesterday}`)
            },

            "tool-tomorrow": async () => {
                try {
                    const today = data.time
                    const tomorrow = TimeUtils.getNextDay(today)
                    await deviceApi.migrate(today, tomorrow)
                    message.success('数据迁移成功')
                    navigate(`/edit/${tomorrow}`)
                } catch (error) {
                    message.error(`迁移失败: ${error.message}`)
                }
            },

            "tool-theme": async () => {
                try {
                    const currentTheme = getCurrentTheme()
                    const newTheme = currentTheme === 'dark' ? 'light' : 'dark'
                    await setThemeUtil(newTheme, deviceApi)
                    message.success('主题切换成功')
                } catch (error) {
                    message.error(`主题切换失败: ${error.message}`)
                }
            },

            "tool-mosaic": () => {
                const blurElements = ref.current?.querySelectorAll('.ne-table-inner-wrap, .container-todo, .container-limit, tbody.ne-table-inner')
                const isBlurred = ref.current?.querySelector('.blur')
                blurElements?.forEach(el => {
                    if (isBlurred) {
                        el.classList.remove('blur')
                    } else {
                        el.classList.add('blur')
                    }
                })
            },

            "tool-calender": () => {
                navigate('/calendar')
            },

            "tool-manage": () => {
                navigate('/manage')
            },

            "tool-users": () => {
                navigate('/users')
            },

            "tool-goals": () => {
                navigate('/goals')
            },

            "tool-venture": () => {
                navigate('/venture')
            },
        }

        const hasEvent = event.hasOwnProperty(eventName)
        if (hasEvent) {
            event[eventName]()
        }
        return hasEvent
    }

    // 快捷键支持
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault()
                handleSave()
            }
            if (e.key === 'Escape') {
                buttonAction('tool-mosaic')
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, noteIsPublic])

    if (loading) {
        return <div className="container">加载中...</div>
    }

    return (
        <div className="container" onInput={inputMethod} ref={ref}>
            <TodoHeader
                time={data.time}
                note={data.note}
                extra2Text={data.extra2Text}
                extra3Links={data.extra3Links}
                inputMethod={inputMethod}
                noteIsPublic={noteIsPublic}
                onNoteVisibilityChange={handleNoteVisibilityChange}
            />
            <TodoTable items={data.items} inputMethod={inputMethod} />
            <TodoTool eventInput={buttonAction} />
        </div>
    )
}

export default Edit
